import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Star, Trash2, MessageSquare } from "lucide-react";
import { adminClient } from "./api";

export default function ReviewsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/admin/reviews");
      setItems(data.items || []);
    } catch { toast.error("Could not load reviews"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Delete this ${r.rating}★ review from ${r.name}?`)) return;
    try {
      await adminClient.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      window.dispatchEvent(new CustomEvent("hp:reviews-changed"));
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleVisible = async (r) => {
    try {
      await adminClient.put(`/admin/reviews/${r.id}/visibility`, { is_visible: !r.is_visible });
      toast.success(r.is_visible ? "Hidden from site" : "Now live on site", { icon: "🟢" });
      window.dispatchEvent(new CustomEvent("hp:reviews-changed"));
      load();
    } catch { toast.error("Update failed"); }
  };

  const avg = items.length ? (items.filter((r) => r.is_visible).reduce((s, r) => s + r.rating, 0) / Math.max(1, items.filter((r) => r.is_visible).length)) : 0;

  return (
    <div data-testid="reviews-admin-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h1 className="font-display font-black text-2xl text-[#0C2E2B]">Reviews</h1>
          <p className="text-sm text-zinc-500">Hide or delete reviews here — the site + banner update instantly.</p>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-[#FFDF00] text-[#0C2E2B] hover:bg-[#FFDF00]">
            {avg > 0 ? `${avg.toFixed(1)}★ avg` : "No visible reviews"}
          </Badge>
          <Badge className="bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]">{items.length} total</Badge>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading...</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center">
          <MessageSquare className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-sm font-bold text-zinc-800">No reviews yet</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <Card key={r.id} data-testid={`review-admin-card-${r.id}`} className={`p-5 border-2 ${r.is_visible ? "border-zinc-200" : "border-zinc-300 bg-zinc-50 opacity-70"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-black text-[#0C2E2B]">{r.name}</div>
                    {!r.is_visible && <Badge variant="outline" className="text-[10px]">HIDDEN</Badge>}
                  </div>
                  <div className="flex mt-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "fill-[#FFDF00] text-[#FFDF00]" : "text-zinc-300"}`} />
                    ))}
                    <span className="ml-2 text-[10px] text-zinc-400">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {r.comment && <p className="mt-2 text-xs text-zinc-700">{r.comment}</p>}
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <div className="flex items-center gap-2 text-xs">
                    <Switch
                      data-testid={`review-admin-toggle-${r.id}`}
                      checked={r.is_visible}
                      onCheckedChange={() => toggleVisible(r)}
                    />
                    <span className="text-zinc-500">{r.is_visible ? "Live" : "Hidden"}</span>
                  </div>
                  <Button
                    data-testid={`review-admin-delete-${r.id}`}
                    size="sm"
                    variant="outline"
                    className="rounded-md h-7 text-[10px] text-red-600 hover:text-red-700"
                    onClick={() => remove(r)}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
